import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Pill, MapPin, Star, Moon, Search, X, Phone } from "lucide-react";
import Navbar from "../components/Navbar";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

// Annuaire public des pharmacies — clone de Laboratoires.js (meme extension
// hors cahier des charges, meme reutilisation de GET /api/clinics avec
// establishment_type=pharmacy, routes/clinics.py), avec un filtre
// supplementaire propre aux pharmacies : "de garde aujourd'hui"
// (garde_today=true, cote backend routes/officine.py pour la declaration
// des jours de garde par le titulaire). Quand ce filtre est actif, TOUTES
// les pharmacies retournees sont de garde aujourd'hui par definition du
// filtre — pas besoin d'un appel supplementaire par fiche pour savoir
// lesquelles afficher avec le badge.
const Pharmacies = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const wilaya = searchParams.get("wilaya") || "";
  const gardeToday = searchParams.get("garde") === "1";
  const name = searchParams.get("name") || "";
  const commune = searchParams.get("commune") || "";
  const [wilayas, setWilayas] = useState([]);
  // Communes REELLEMENT presentes parmi les pharmacies de la wilaya
  // choisie (GET /api/clinics/communes, ajoute 2026-09-06) — meme principe
  // que le filtre daira/commune de la recherche medecin (SearchPage.js),
  // mais base sur les pharmacies elles-memes plutot que sur db.doctors qui
  // ne les contient pas.
  const [communes, setCommunes] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // Constat Nadjib (2026-09-06, apres l'import 1sante.com qui fait passer
  // l'annuaire a 3609 pharmacies) : impossible de retrouver une pharmacie
  // precise sans feuilleter les pages une par une, seul le filtre wilaya
  // existait. `nameInput` = texte tape en direct (affichage immediat) ;
  // `name` (derive de l'URL) = valeur REELLEMENT recherchee, mise a jour
  // avec un leger delai (voir le useEffect de debounce ci-dessous) pour ne
  // pas interroger l'API a chaque frappe.
  const [nameInput, setNameInput] = useState(name);
  const debounceRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/wilayas`).then((res) => setWilayas(res.data || [])).catch(() => setWilayas([]));
  }, []);

  // Recharge la liste des communes a chaque changement de wilaya (vide si
  // aucune wilaya choisie — le filtre commune reste desactive dans ce cas,
  // comme le filtre daira de la recherche medecin).
  useEffect(() => {
    if (!wilaya) {
      setCommunes([]);
      return;
    }
    axios.get(`${API_URL}/api/clinics/communes`, { params: { wilaya, establishment_type: "pharmacy" } })
      .then((res) => setCommunes(res.data || []))
      .catch(() => setCommunes([]));
  }, [wilaya]);

  // Garde `nameInput` synchronise si `name` change par une autre voie que
  // la frappe (ex. navigation arriere/avant du navigateur, lien externe
  // avec ?name=...).
  useEffect(() => {
    setNameInput(name);
  }, [name]);

  const fetchPharmacies = useCallback(async (wilayaCode, garde, searchName, communeName, pageNum) => {
    setLoading(true);
    try {
      const params = { establishment_type: "pharmacy", page: pageNum, limit: PAGE_SIZE };
      if (wilayaCode) params.wilaya = wilayaCode;
      if (garde) params.garde_today = true;
      if (searchName) params.name = searchName;
      // routes/clinics.py::list_clinics_public filtre `daira` par un OR
      // regex sur les champs daira ET commune — le meme parametre convient
      // donc pour un filtre "commune" cote pharmacie.
      if (communeName) params.daira = communeName;
      const res = await axios.get(`${API_URL}/api/clinics`, { params });
      setPharmacies(res.data.clinics || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setPharmacies([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchPharmacies(wilaya, gardeToday, name, commune, 1);
  }, [wilaya, gardeToday, name, commune, fetchPharmacies]);

  const buildParams = (overrides) => {
    const next = {};
    const merged = { wilaya, garde: gardeToday ? "1" : "", name, commune, ...overrides };
    if (merged.wilaya) next.wilaya = merged.wilaya;
    if (merged.garde === "1") next.garde = "1";
    if (merged.name) next.name = merged.name;
    if (merged.commune) next.commune = merged.commune;
    return next;
  };

  const changeWilaya = (code) => {
    // Changer de wilaya invalide forcement la commune choisie (elle
    // appartenait a l'ancienne wilaya) — meme comportement que le filtre
    // daira de la recherche medecin.
    setSearchParams(buildParams({ wilaya: code, commune: "" }));
  };

  const changeCommune = (value) => {
    setSearchParams(buildParams({ commune: value }));
  };

  const toggleGarde = () => {
    setSearchParams(buildParams({ garde: gardeToday ? "" : "1" }));
  };

  // Recherche par nom : debounce de 400ms — evite d'interroger l'API a
  // chaque caractere tape (annuaire de 3609 pharmacies).
  const changeNameInput = (value) => {
    setNameInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchParams(buildParams({ name: value.trim() }));
    }, 400);
  };

  const clearNameInput = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setNameInput("");
    setSearchParams(buildParams({ name: "" }));
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const changePage = (p) => {
    setPage(p);
    fetchPharmacies(wilaya, gardeToday, name, commune, p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Helmet>
        <title>Trouver une pharmacie {gardeToday ? "de garde " : ""}| Tabbibak</title>
        <meta
          name="description"
          content="Trouvez une pharmacie en Algérie, y compris les pharmacies de garde aujourd'hui, par wilaya."
        />
        <link rel="canonical" href="https://tabbibak.com/pharmacies" />
      </Helmet>

      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-[#1A2B3C] mb-2 flex items-center gap-2">
            <Pill className="w-6 h-6 text-[#0596DE]" />
            Trouver une pharmacie
          </h1>
          <p className="text-[#64748B] mb-6">
            {loading ? "Chargement..." : `${total} pharmacie(s) trouvée(s)`}
          </p>

          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={nameInput}
                onChange={(e) => changeNameInput(e.target.value)}
                placeholder="Rechercher une pharmacie par nom..."
                className="w-full border border-[#E2E8F0] rounded-lg pl-10 pr-9 py-2.5 text-[#1A2B3C] bg-white placeholder:text-[#94A3B8]"
              />
              {nameInput && (
                <button
                  onClick={clearNameInput}
                  aria-label="Effacer la recherche"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1A2B3C]"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <select
              value={wilaya}
              onChange={(e) => changeWilaya(e.target.value)}
              className="w-full sm:w-72 border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#1A2B3C] bg-white"
            >
              <option value="">Toutes les wilayas</option>
              {wilayas.map((w) => (
                <option key={w.code} value={w.code}>{w.code} - {w.name_fr}</option>
              ))}
            </select>
            <select
              value={commune}
              onChange={(e) => changeCommune(e.target.value)}
              disabled={!wilaya}
              className="w-full sm:w-64 border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#1A2B3C] bg-white disabled:bg-[#F1F5F9] disabled:text-[#94A3B8]"
            >
              <option value="">{wilaya ? "Toutes les communes" : "Choisir une wilaya d'abord"}</option>
              {communes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              onClick={toggleGarde}
              className={`shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                gardeToday
                  ? "bg-[#0596DE] text-white border-[#0596DE]"
                  : "bg-white text-[#1A2B3C] border-[#E2E8F0] hover:border-[#0596DE]"
              }`}
            >
              <Moon className="w-4 h-4" />
              De garde aujourd'hui
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : pharmacies.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-[#64748B]">
              {name
                ? `Aucune pharmacie ne correspond à "${name}"${gardeToday ? " (de garde aujourd'hui)" : ""}.`
                : gardeToday
                ? "Aucune pharmacie de garde déclarée aujourd'hui pour cette recherche."
                : "Aucune pharmacie trouvée pour cette recherche."}
            </div>
          ) : (
            <div className="space-y-4">
              {pharmacies.map((pharmacy) => (
                <div
                  key={pharmacy.clinic_id}
                  className="bg-white rounded-xl border border-[#E2E8F0] p-6 flex items-center justify-between gap-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/pharmacie/${pharmacy.slug}`)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-14 h-14 shrink-0 rounded-xl bg-[#E6F4FB] flex items-center justify-center text-[#0596DE]">
                      <Pill className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-semibold text-[#1A2B3C] truncate">{pharmacy.name}</h2>
                        {gardeToday && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E0F2FE] text-[#0596DE] text-xs font-medium shrink-0">
                            <Moon className="w-3 h-3" /> De garde
                          </span>
                        )}
                      </div>
                      {pharmacy.address && (
                        <p className="text-[#64748B] text-sm flex items-center gap-1 truncate">
                          <MapPin className="w-3.5 h-3.5 shrink-0" /> {pharmacy.address}
                        </p>
                      )}
                      {pharmacy.phone && (
                        <a
                          href={`tel:${pharmacy.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[#64748B] text-sm flex items-center gap-1 truncate hover:text-[#0596DE] w-fit"
                        >
                          <Phone className="w-3.5 h-3.5 shrink-0" /> {pharmacy.phone}
                        </a>
                      )}
                      {pharmacy.review_count > 0 && (
                        <p className="text-sm flex items-center gap-1 text-[#1A2B3C]">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          {pharmacy.average_rating} <span className="text-[#64748B]">({pharmacy.review_count} avis)</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    className="shrink-0 bg-[#0596DE] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#047CBA] transition-colors"
                    onClick={(e) => { e.stopPropagation(); navigate(`/pharmacie/${pharmacy.slug}`); }}
                  >
                    Voir la fiche
                  </button>
                </div>
              ))}
            </div>
          )}

          {!loading && pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => changePage(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium ${
                    p === page ? "bg-[#0596DE] text-white" : "bg-white border border-[#E2E8F0] text-[#1A2B3C]"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Pharmacies;
